import React, { useState, useEffect } from 'react'
import { Link } from 'react-router-dom'
import {X} from 'lucide-react'

interface BannerMessage {
  text: string
  badge: string
  badgeColor: string
  bgColor: string
  textColor: string
  borderColor: string
  ctaText: string
}

const AnnouncementBanner = () => {
  const [isVisible, setIsVisible] = useState(true)
  const [bannerData, setBannerData] = useState<BannerMessage | null>(null)

  const calculateBannerMessage = (): BannerMessage | null => {
    const now = new Date()
    const freeEndDate = new Date('2025-12-31T23:59:59')
    const fiveDollarEndDate = new Date('2026-03-31T23:59:59')
    const tenDollarStartDate = new Date('2026-04-01T00:00:00')

    // Phase 1: FREE (Before Jan 1, 2026)
    if (now <= freeEndDate) {
      const daysLeft = Math.ceil((freeEndDate.getTime() - now.getTime()) / (1000 * 60 * 60 * 24))
      return {
        text: `Launching Date: 1st Jan, 2026. Pre-Launch registration is FREE until Dec 31, 2025`,
        badge: `${daysLeft} days left`,
        badgeColor: 'bg-teal-600 text-white',
        bgColor: 'bg-[#E6FCF5]',
        textColor: 'text-[#0F5132]',
        borderColor: 'border-[#BFF0E3]',
        ctaText: 'Register Free / Sign In'
      }
    }

    // Phase 2: $5 (Jan 1 - Mar 31, 2026)
    if (now > freeEndDate && now <= fiveDollarEndDate) {
      const daysLeft = Math.ceil((fiveDollarEndDate.getTime() - now.getTime()) / (1000 * 60 * 60 * 24))
      return {
        text: `Pricing: $5 from Jan 1, 2026 • $10 from Apr 1, 2026`,
        badge: `${daysLeft} days left`,
        badgeColor: 'bg-amber-600 text-white',
        bgColor: 'bg-amber-50',
        textColor: 'text-amber-900',
        borderColor: 'border-amber-200',
        ctaText: 'Register ($5)'
      }
    }

    // Phase 3: $10 (Apr 1, 2026+)
    if (now >= tenDollarStartDate) {
      return {
        text: `Pricing: $10 from Apr 1, 2026 (Lifetime access)`,
        badge: 'Lifetime',
        badgeColor: 'bg-blue-600 text-white',
        bgColor: 'bg-blue-50',
        textColor: 'text-blue-900',
        borderColor: 'border-blue-200',
        ctaText: 'Register ($10)'
      }
    }

    return null
  }

  const checkDismissalStatus = () => {
    const dismissedTime = localStorage.getItem('nursing-hive-banner-dismissed')
    if (dismissedTime) {
      const timePassed = Date.now() - parseInt(dismissedTime, 10)
      const twentyFourHours = 24 * 60 * 60 * 1000
      
      if (timePassed >= twentyFourHours) {
        localStorage.removeItem('nursing-hive-banner-dismissed')
        setIsVisible(true)
      } else {
        setIsVisible(false)
      }
    }
  }

  const handleDismiss = () => {
    localStorage.setItem('nursing-hive-banner-dismissed', Date.now().toString())
    setIsVisible(false)
  }

  useEffect(() => {
    // Check dismissal status on mount
    checkDismissalStatus()
    
    // Calculate initial banner message
    const message = calculateBannerMessage()
    setBannerData(message)

    // Update banner message every 60 seconds
    const interval = setInterval(() => {
      const updatedMessage = calculateBannerMessage()
      setBannerData(updatedMessage)
    }, 60000)

    return () => clearInterval(interval)
  }, [])

  if (!isVisible || !bannerData) return null

  return (
    <div className={`${bannerData.bgColor} border-b ${bannerData.borderColor} relative`}>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-2.5">
        <div className={`flex items-center justify-between gap-3 ${bannerData.textColor} text-sm`}>
          <div className="flex-1 flex items-center justify-center gap-2 flex-wrap">
            <span className="font-semibold">
              {bannerData.text}
            </span>
            <span className={`${bannerData.badgeColor} px-2 py-0.5 rounded text-xs font-bold`}>
              {bannerData.badge}
            </span>
          </div>
          
          <div className="flex items-center gap-2 flex-shrink-0">
            <Link
              to="/signup"
              className="bg-primary-teal text-white px-4 py-1.5 rounded-md font-semibold hover:bg-teal-700 transition-colors whitespace-nowrap text-sm"
            >
              {bannerData.ctaText}
            </Link>
            <button
              onClick={handleDismiss}
              className={`${bannerData.textColor} hover:opacity-70 transition-opacity`}
              aria-label="Close announcement"
            >
              <X className="h-4 w-4" />
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}

export default AnnouncementBanner
